import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../shared/Button";
import { useFilterStore } from "../../store/filterStore";
import type { InstanceCategory } from "../../store/filterStore";
import { useInstanceStore } from "../../store/useInstanceStore";
import SmartFiltersAccordion from "./SmartFiltersAccordion";

// Icons
const SearchIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="absolute left-3 text-gray-400 pointer-events-none"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const FilterIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const InstanceTypeFilter: React.FC = () => {
  const category = useFilterStore((state) => state.category);
  const setCategory = useFilterStore((state) => state.setCategory);
  const data = useInstanceStore((state) => state.data);
  const setFilterData = useInstanceStore((state) => state.setFilterData);

  const categories: InstanceCategory[] = useMemo(
    () => ["All Types", "General Purpose", "Compute Purpose", "Memory Purpose"],
    []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isSmartFiltersOpen, setIsSmartFiltersOpen] = useState(false);
  const smartFiltersButtonRef = React.useRef<HTMLButtonElement>(null);

  // 🔹 Debounce input — wait 400ms after typing stops
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim().toLowerCase());
    }, 400);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (!data) return;
    if (!debouncedQuery) {
      setFilterData(data);
    } else {
      const filtered = data.filter((item) =>
        item.instanceType.toLowerCase().includes(debouncedQuery)
      );
      setFilterData(filtered);
    }
  }, [debouncedQuery, data, setFilterData]);

  // 🔹 Handle input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const handleRadioChange = useCallback(
    (cat: InstanceCategory) => setCategory(cat),
    [setCategory]
  );

  const handleSmartFiltersToggle = useCallback(() => {
    setIsSmartFiltersOpen((prev) => !prev);
  }, []);

  return (
    <div className="w-full relative">
      <div className="flex flex-wrap items-center gap-5 p-3 justify-between bg-white border border-gray-200 rounded-lg w-full  min-h-[52px]">
        <div className="flex flex-1 min-w-[200px] max-w-[600px] relative items-center">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search Instance types..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex flex-1 sm:flex-none flex-wrap items-center justify-end gap-3 min-w-fit">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-gray-700 font-medium text-sm whitespace-nowrap">
              Instance Type
            </span>
            {categories.map((cat) => {
              const isSelected = category === cat;
              const id = `instance-type-${cat.toLowerCase().replace(/\s/g, "-")}`;
              return (
                <label
                  key={cat}
                  htmlFor={id}
                  className="flex items-center gap-1.5 text-gray-700 text-sm cursor-pointer select-none whitespace-nowrap"
                >
                  <input
                    type="radio"
                    id={id}
                    name="instance-type"
                    value={cat}
                    checked={isSelected}
                    onChange={() => handleRadioChange(cat)}
                    className="sr-only"
                  />
                  <span
                    className={`flex items-center justify-center w-4 h-4 border-2 rounded-full transition-all ${
                      isSelected ? "border-blue-500 bg-white" : "border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full" />
                    )}
                  </span>
                  {cat}
                </label>
              );
            })}
          </div>

          <Button
            ref={smartFiltersButtonRef}
            variant="dark"
            size="md"
            leftIcon={<FilterIcon />}
            onClick={handleSmartFiltersToggle}
            className="flex-shrink-0"
          >
            Smart Filters
          </Button>
        </div>
      </div>
      <SmartFiltersAccordion
        isOpen={isSmartFiltersOpen}
        onClose={() => setIsSmartFiltersOpen(false)}
        buttonRef={smartFiltersButtonRef}
        showCPUArchitectureFilter={true}
      />
    </div>
  );
};

export default InstanceTypeFilter;
